import AddIcon from '@mui/icons-material/Add'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import GroupIcon from '@mui/icons-material/Group'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import type { GridApi, GridFilterModel } from '@mui/x-data-grid'
import { useGridApiRef } from '@mui/x-data-grid'
import { List } from '@refinedev/mui'
import { memo, useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { fetchScouts, useScoutsQuery } from '../api'
import type { ScoutStatus } from '../types'
import { exportScouts } from '../utils.ts'

import type { ScoutListProps } from './ScoutList.types'
import { ScoutTable } from './ScoutTable'

import { getResourceConfig } from '@/app/config/resources'

export const ScoutList = memo<ScoutListProps>(({ initialFilters, onScoutSelect }) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const theme = useTheme()
  const resourceConfig = getResourceConfig('scout')
  const apiRef = useGridApiRef<GridApi>()

  // Initialize state from URL params
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page')
    return pageParam ? Math.max(0, parseInt(pageParam, 10) - 1) : 0 // Convert from 1-based to 0-based
  })
  const [pageSize, setPageSize] = useState(() => {
    const size = searchParams.get('pageSize')
    return size ? parseInt(size, 10) : 10
  })
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(
    () => searchParams.get('q') || ''
  )
  const [filterModel, setFilterModel] = useState<GridFilterModel>(() => {
    // Get all status parameters from URL
    const statusValues = searchParams.getAll('status')

    // If URL has status parameters, use them (including 'all' if present)
    if (statusValues.length > 0) {
      return {
        items: [{ field: 'status', operator: 'isAnyOf', value: statusValues }],
      }
    }

    // If no URL parameters, default to new and Aktiv
    return {
      items: [{ field: 'status', operator: 'isAnyOf', value: ['new', 'Aktiv'] }],
    }
  })

  // Debounce search query to avoid too many API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      const hasSearchChanged = searchQuery !== debouncedSearchQuery
      setDebouncedSearchQuery(searchQuery)

      // Reset page if search query has changed
      if (hasSearchChanged) {
        setPage(0)
      }
    }, 300)

    return () => {
      clearTimeout(handler)
    }
  }, [searchQuery, debouncedSearchQuery])

  // Update URL when state changes
  useEffect(() => {
    const params = new URLSearchParams()

    if (page > 0) params.set('page', (page + 1).toString()) // Convert from 0-based to 1-based for URL
    if (pageSize !== 10) params.set('pageSize', pageSize.toString())
    if (debouncedSearchQuery) params.set('q', debouncedSearchQuery)

    // Extract status from filter model
    const statusFilterItem = filterModel.items.find(item => item.field === 'status')
    if (statusFilterItem?.value) {
      // Handle both single values and arrays (isAnyOf operator)
      const statusValues = Array.isArray(statusFilterItem.value)
        ? statusFilterItem.value
        : [statusFilterItem.value]

      // Add all selected statuses to URL (including 'all')
      statusValues.forEach((status, index) => {
        if (status) {
          if (index === 0) {
            params.set('status', status as string)
          } else {
            params.append('status', status as string)
          }
        }
      })
    }

    setSearchParams(params, { replace: true })
  }, [page, pageSize, debouncedSearchQuery, filterModel, setSearchParams])

  // Extract status from filter model for API call
  const statusFilterItem = filterModel.items.find(item => item.field === 'status')
  let filterValues: string[] = []
  if (statusFilterItem?.value) {
    filterValues = Array.isArray(statusFilterItem.value)
      ? (statusFilterItem.value as string[])
      : [statusFilterItem.value as string]
  }

  // Filter out 'all' status - it's frontend only
  const statusFilter = filterValues.filter(status => status !== 'all') as ScoutStatus[]

  const { data, isLoading, refetch, isRefetching, dataUpdatedAt } = useScoutsQuery({
    ...initialFilters,
    limit: pageSize,
    offset: page * pageSize,
    q: debouncedSearchQuery || undefined,
    status: statusFilter,
  })

  // Track total count, only update when data?.total is not undefined
  const [totalCount, setTotalCount] = useState(0)
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date())

  // Update total count only when data?.total is defined
  useEffect(() => {
    if (data?.total !== undefined && typeof data.total === 'number') {
      setTotalCount(data.total)
    }
  }, [data?.total])

  // Update lastUpdated timestamp whenever data is fetched (using dataUpdatedAt from React Query)
  useEffect(() => {
    if (dataUpdatedAt) {
      setLastUpdated(new Date(dataUpdatedAt))
    }
  }, [dataUpdatedAt])

  const handleRowClick = useCallback(
    (scoutId: string) => {
      if (onScoutSelect) {
        onScoutSelect(scoutId)
      } else {
        navigate(`/scouts/show/${scoutId}`)
      }
    },
    [navigate, onScoutSelect]
  )

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage)
  }, [])

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      if (newPageSize !== pageSize) {
        setPageSize(newPageSize)
        setPage(0) // Reset to first page when changing page size
      }
    },
    [pageSize]
  )

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setSearchQuery(value)
    // Note: Page reset happens in the debounced search effect when debouncedSearchQuery changes
  }, [])

  const handleFilterModelChange = useCallback(
    (newModel: GridFilterModel) => {
      // Compare current and new filter models to see if they're different
      const currentStatusFilter = filterModel.items.find(item => item.field === 'status')
      const newStatusFilter = newModel.items.find(item => item.field === 'status')

      // Handle status filter logic
      let processedModel = { ...newModel }
      if (newStatusFilter) {
        let filterValues = Array.isArray(newStatusFilter.value)
          ? newStatusFilter.value
          : [newStatusFilter.value]

        // Remove empty/null values
        filterValues = filterValues.filter(v => v)

        // If no values are selected, set to 'all'
        if (filterValues.length === 0) {
          processedModel = {
            ...newModel,
            items: newModel.items.map(item =>
              item.field === 'status' ? { ...item, value: ['all'] } : item
            ),
          }
        }
        // If 'all' is selected along with other statuses, remove 'all'
        else if (filterValues.includes('all') && filterValues.length > 1) {
          const withoutAll = filterValues.filter(status => status !== 'all')
          processedModel = {
            ...newModel,
            items: newModel.items.map(item =>
              item.field === 'status' ? { ...item, value: withoutAll } : item
            ),
          }
        }
      }

      // Compare the filter values (could be arrays for isAnyOf operator)
      const currentValue = JSON.stringify(currentStatusFilter?.value || [])
      const newValue = JSON.stringify(
        processedModel.items.find(item => item.field === 'status')?.value || []
      )

      setFilterModel(processedModel)

      // Only reset page if filter value has actually changed
      if (currentValue !== newValue) {
        setPage(0)
      }
    },
    [filterModel]
  )

  const handleFilterClick = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.showFilterPanel('status')
    }
  }, [apiRef])

  // Export menu state
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null)
  const [isExporting, setIsExporting] = useState(false)

  const handleExportClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setExportAnchorEl(event.currentTarget)
  }, [])

  const handleExportClose = useCallback(() => {
    setExportAnchorEl(null)
  }, [])

  const handleExport = useCallback(
    async (format: 'csv' | 'xlsx') => {
      handleExportClose()
      setIsExporting(true)
      try {
        // Fetch all scouts matching the current search and status filters
        const result = await fetchScouts({
          ...initialFilters,
          limit: totalCount,
          offset: 0,
          q: debouncedSearchQuery || undefined,
          status: statusFilter,
        })
        exportScouts(result.data, format)
      } catch (error) {
        console.error('Failed to export scouts:', error)
      } finally {
        setIsExporting(false)
      }
    },
    [handleExportClose, initialFilters, totalCount, debouncedSearchQuery, statusFilter]
  )

  // Generate status text
  const getStatusText = useCallback(() => {
    const parts: (string | React.ReactElement)[] = []

    // Scout count
    const totalScouts = totalCount
    parts.push(`${totalScouts} scouts`)

    // Filtering info
    const statusFilterItem = filterModel.items.find(item => item.field === 'status')
    const filterValues = statusFilterItem?.value
    if (filterValues && Array.isArray(filterValues) && filterValues.includes('all')) {
      // When 'all' is selected, don't show filtering info
    } else if (statusFilter && statusFilter.length > 0) {
      const filterText = statusFilter.join(', ')
      parts.push(
        <span key="status-filter" onClick={handleFilterClick} style={{ cursor: 'pointer' }}>
          Filtered by Status:{' '}
          <span style={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
            {filterText}
          </span>
        </span>
      )
    }

    // Last updated
    const now = new Date()
    const diffMs = now.getTime() - lastUpdated.getTime()
    const diffSeconds = Math.floor(diffMs / 1000)
    const diffMinutes = Math.floor(diffSeconds / 60)
    const diffHours = Math.floor(diffMinutes / 60)

    let timeText = ''
    if (diffSeconds < 60) {
      timeText = 'a few seconds ago'
    } else if (diffMinutes < 60) {
      timeText = `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`
    } else if (diffHours < 24) {
      timeText = `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    } else {
      timeText = lastUpdated.toLocaleDateString()
    }
    parts.push(`Updated ${timeText}`)

    return parts.map((part, index) => (
      <span key={index}>
        {part}
        {index < parts.length - 1 && ' • '}
      </span>
    ))
  }, [
    totalCount,
    statusFilter,
    lastUpdated,
    filterModel.items,
    theme.palette.primary.main,
    handleFilterClick,
  ])

  // Update status text every minute to keep "Updated X ago" current
  const [, setUpdateTrigger] = useState(0)
  useEffect(() => {
    const interval = setInterval(() => {
      setUpdateTrigger(prev => prev + 1)
    }, 60000) // Update every minute

    return () => clearInterval(interval)
  }, [])

  return (
    <List
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon sx={{ color: theme.palette.primary.main }} />
          <span>Scout Management</span>
        </Box>
      }
      headerButtons={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => refetch()}
              disabled={isRefetching}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <RefreshIcon
                sx={{
                  fontSize: 20,
                  animation: isRefetching ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportClick}
            disabled={isExporting || totalCount === 0}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={handleExportClose}
          >
            <MenuItem onClick={() => void handleExport('xlsx')}>Export as Excel (.xlsx)</MenuItem>
            <MenuItem onClick={() => void handleExport('csv')}>Export as CSV (.csv)</MenuItem>
          </Menu>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/scouts/create')}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
            }}
          >
            Create Scout
          </Button>
        </Box>
      }
    >
      <Paper
        elevation={0}
        sx={{
          p: 2,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        {/* Search Input */}
        <Box sx={{ mb: 2 }}>
          <TextField
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search scouts by name, email, phone, or address..."
            size="small"
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
        </Box>

        {/* Status Text */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: '0.875rem',
              lineHeight: 1.4,
            }}
          >
            {getStatusText()}
          </Typography>
        </Box>

        <Box sx={{ minHeight: 400 }}>
          <ScoutTable
            scouts={data?.data || []}
            loading={isLoading || isRefetching}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onRowClick={handleRowClick}
            onFilterModelChange={handleFilterModelChange}
            filterModel={filterModel}
            canDelete={resourceConfig?.meta?.canDelete}
            apiRef={apiRef}
          />
        </Box>
      </Paper>
    </List>
  )
})

ScoutList.displayName = 'ScoutList'
